import React from 'react';
import { useSelector } from 'react-redux';
import type { Dayjs } from 'dayjs';
import { getWebsitePreviewWeddingValues } from 'selectors/website/websitePreviewSelectors';
import type { WeddingPropType } from '../../types';
import {
  mapWeddingAccountToWebsiteDateProp,
  mapWebsitePreviewWeddingValuesToWebsiteDateProp,
} from '../../../../../../util/websiteDateUtil';
import { HorizontalContainer, WeddingInfoSectionContainer } from './WeddingInfoSection.styles';

import {
  WeddingDateHorizontal,
  WeddingDateVerticalSingle,
  WeddingDateVerticalDouble,
} from '../WeddingDateVariants';

type WeddingInfoSectionProps = {
  hashtag?: string;
  weddingDate?: Dayjs;
  weddingCity?: string;
  weddingStateProvince?: string;
  wedding: WeddingPropType;
  themeKey?: string;
  renderVideo: () => JSX.Element | null;
};

const WeddingInfoSection = ({
  hashtag,
  weddingDate,
  weddingCity,
  weddingStateProvince,
  wedding,
  themeKey,
  renderVideo,
}: WeddingInfoSectionProps): JSX.Element => {
  const websitePreviewWeddingValues = useSelector(getWebsitePreviewWeddingValues);
  const websiteDate =
    mapWebsitePreviewWeddingValuesToWebsiteDateProp(websitePreviewWeddingValues, undefined) ||
    mapWeddingAccountToWebsiteDateProp(wedding, undefined);

  const themeRangeStyleOverrideProps = {
    themeGroup: (themeKey || '').split('-')[0],
    displayFormat: websiteDate.displayFormat,
  };

  return (
    <WeddingInfoSectionContainer className="wedding-info-section" {...themeRangeStyleOverrideProps}>
      <HorizontalContainer className="horizontal" {...themeRangeStyleOverrideProps}>
        {hashtag && (
          <div className="hashtag">
            <h4>#{hashtag}</h4>
          </div>
        )}
        <div className="content">
          {(weddingCity || weddingStateProvince) && (
            <div className="location">
              <h4 className="in">in</h4>
              <h3>
                {weddingCity && <span className="city">{weddingCity}</span>}
                {weddingStateProvince && weddingCity && <span className="comma">, </span>}
                {weddingStateProvince && <span className="state">{weddingStateProvince}</span>}
              </h3>
            </div>
          )}
          {weddingDate && <WeddingDateHorizontal websiteDate={websiteDate} />}
        </div>
      </HorizontalContainer>
      <div className="vertical-single">
        <div className="name-1 hidden-xs">
          <div className="initials">
            {wedding.owner_first_name && (
              <span className="initial-1">{wedding.owner_first_name[0]}</span>
            )}
            {wedding.owner_first_name && wedding.owner_last_name && <div className="accent" />}
            {wedding.owner_last_name && (
              <span className="initial-2">{wedding.owner_last_name[0]}</span>
            )}
          </div>
        </div>
        <div className="info-section">
          <div className="content">
            {hashtag && (
              <div className="hashtag">
                <h4>#{hashtag}</h4>
              </div>
            )}
            {weddingDate && <WeddingDateVerticalSingle websiteDate={websiteDate} />}
            {(weddingCity || weddingStateProvince) && (
              <h4 className="location">
                {weddingCity && weddingCity}
                {weddingStateProvince && weddingCity && ', '}
                {weddingStateProvince && weddingStateProvince}
              </h4>
            )}
          </div>
        </div>
        <div className="name-2 hidden-xs">
          <div className="initials">
            {wedding.partner_first_name && (
              <span className="initial-1">{wedding.partner_first_name[0]}</span>
            )}
            {wedding.partner_first_name && wedding.partner_last_name && <div className="accent" />}
            {wedding.partner_last_name && (
              <span className="initial-2">{wedding.partner_last_name[0]}</span>
            )}
          </div>
        </div>
      </div>
      <div className="vertical-double">
        {wedding.owner_first_name && wedding.partner_first_name && (
          <div className="wedding-info-column wedding-info-left">
            {(weddingCity || weddingStateProvince) && (
              <h4 className="location">
                {weddingCity && weddingCity}
                {weddingStateProvince && weddingCity && ', '}
                {weddingStateProvince && weddingStateProvince}
              </h4>
            )}
            <span className="big-initial">{wedding.owner_first_name[0]}</span>
            <h4 className="and">and</h4>
            <span className="big-initial">{wedding.partner_first_name[0]}</span>
            {hashtag && <h4 className="hashtag">#{hashtag}</h4>}
          </div>
        )}
        {weddingDate && <WeddingDateVerticalDouble websiteDate={websiteDate} />}
      </div>
      {renderVideo()}
    </WeddingInfoSectionContainer>
  );
};

export default WeddingInfoSection;
